import EnumValueNotFoundException from '~/errors/EnumValueNotFoundException';

import {
  type PermissionCreatedObject,
  PermissionsObject,
} from '../Permissions';

import {
  PERMISSION_GRANT_ENTITY_TYPE,
  PERMISSION_GRANT_ENTITY_TYPES,
  PERMISSION_GRANT_SUBJECT_TYPE,
  PERMISSION_GRANT_SUBJECT_TYPES,
  PERMISSION_GRANT_TYPE,
} from './constants';
import { type PermissionGrant, type PermissionGrantSubjectType } from './types';

export const PermissionGrantObject = {
  /**
   * Checks if two permission grants are identical by comparing their subject ID, target ID, and permissions.
   */
  arePermissionGrantsMatching(
    permissionGrantA: PermissionGrant,
    permissionGrantB: PermissionGrant,
  ) {
    return (
      permissionGrantA.subjectId === permissionGrantB.subjectId &&
      permissionGrantA.targetId === permissionGrantB.targetId &&
      JSON.stringify(permissionGrantA.permissions) ===
        JSON.stringify(permissionGrantB.permissions)
    );
  },

  /**
   * Checks if the given permissions allow reading delivery notes.
   */
  canReadDeliveryNotes(permission: PermissionCreatedObject) {
    return permission.canReadDeliveryNotes?.();
  },

  /**
   * Creates a formatted permission grant object with processed permissions.
   */
  create(permissionGrant: Partial<PermissionGrant> = {}) {
    const permissions = PermissionsObject.create(permissionGrant.permissions);

    return {
      entityId: permissionGrant?.targetId ?? undefined,
      entityName: permissionGrant?.targetName ?? undefined,
      entityType: permissionGrant?.targetType ?? undefined,
      id: permissionGrant?.id?.toString() ?? undefined,
      permissions,
      roleId: permissionGrant?.roleId ?? undefined,
      roleName: permissionGrant?.roleName ?? undefined,
      subjectId: permissionGrant?.subjectId ?? undefined,
      subjectName: permissionGrant?.subjectName ?? undefined,
      subjectType: permissionGrant?.subjectType ?? undefined,
    };
  },

  ENTITY_TYPE: PERMISSION_GRANT_ENTITY_TYPE,

  ENTITY_TYPES: PERMISSION_GRANT_ENTITY_TYPES,

  /**
   * Retrieves the default role name from the given permissions.
   */
  getDefaultRoleName(permission: PermissionCreatedObject) {
    return permission.getDefaultRoleName?.();
  },

  /**
   * Converts an entity type to its corresponding string representation. Throws an error if invalid.
   */
  getEntityTypeString(entityType: string) {
    if (!Object.keys(PERMISSION_GRANT_ENTITY_TYPES).includes(entityType)) {
      throw new EnumValueNotFoundException(
        `Invalid entity type: ${entityType}`,
      );
    }

    const translatedEntityTypeName =
      PERMISSION_GRANT_ENTITY_TYPES[
        entityType as keyof typeof PERMISSION_GRANT_ENTITY_TYPES
      ];

    return translatedEntityTypeName;
  },

  /**
   * Converts a subject type key to its corresponding string representation. Throws an error if invalid.
   */
  getSubjectTypeString(subjectType: string) {
    const subjectTypeKey = Object.keys(PERMISSION_GRANT_SUBJECT_TYPE).find(
      (key) =>
        PERMISSION_GRANT_SUBJECT_TYPE[key as PermissionGrantSubjectType] ===
        subjectType,
    );

    if (!subjectTypeKey) {
      throw new EnumValueNotFoundException(
        `Invalid subject type: ${subjectType}`,
      );
    }

    return PERMISSION_GRANT_SUBJECT_TYPE[
      subjectTypeKey as PermissionGrantSubjectType
    ];
  },

  SUBJECT_TYPE: PERMISSION_GRANT_SUBJECT_TYPE,

  SUBJECT_TYPES: PERMISSION_GRANT_SUBJECT_TYPES,

  TYPE: PERMISSION_GRANT_TYPE,
};
